import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Grade,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  TrendingUp,
  ThumbUp,
  Schedule,
  RuleFolder
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ResponsiveRadar } from '@nivo/radar';
import { advancedReportsAPI } from '../../services/api';

interface QualityScore {
  conversation_id: number;
  contact_name: string;
  agent_name: string;
  overall_score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  detailed_scores: {
    completeness: number;
    tone: number;
    resolution: number;
    guidelines: number;
    timeliness: number;
  };
  date: string;
}

interface QualityMetrics {
  average_score: number;
  grade_distribution: {
    A: number;
    B: number;
    C: number;
    D: number;
    F: number;
  };
  conversations: QualityScore[];
  top_performers: Array<{
    agent_name: string;
    avg_score: number;
    conversations_reviewed: number;
  }>;
  improvement_areas: Array<{
    criteria: string;
    avg_score: number;
    priority: 'High' | 'Medium' | 'Low';
  }>;
}

interface ConversationQualityScoringProps {
  startDate: string;
  endDate: string;
}

const ConversationQualityScoring: React.FC<ConversationQualityScoringProps> = ({
  startDate,
  endDate
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QualityMetrics | null>(null);

  useEffect(() => {
    fetchQualityData();
  }, [startDate, endDate]);

  const fetchQualityData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advancedReportsAPI.getConversationQuality({
        start_date: startDate,
        end_date: endDate
      });

      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch quality data:', err);
      setError(err.message || 'Failed to load quality scoring data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No quality scoring data available.</Alert>;
  }

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return '#4caf50';
      case 'B': return '#8bc34a';
      case 'C': return '#ff9800';
      case 'D': return '#ff5722';
      case 'F': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const gradeChartOptions: ApexOptions = {
    chart: {
      type: 'donut'
    },
    labels: ['A', 'B', 'C', 'D', 'F'],
    colors: ['#4caf50', '#8bc34a', '#ff9800', '#ff5722', '#f44336'],
    legend: {
      position: 'bottom'
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => String(data.conversations.length)
            }
          }
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(0)}%`
    }
  };

  const radarData = [
    {
      criteria: 'Completeness',
      score: data.conversations.reduce((sum, c) => sum + c.detailed_scores.completeness, 0) / data.conversations.length
    },
    {
      criteria: 'Tone',
      score: data.conversations.reduce((sum, c) => sum + c.detailed_scores.tone, 0) / data.conversations.length
    },
    {
      criteria: 'Resolution',
      score: data.conversations.reduce((sum, c) => sum + c.detailed_scores.resolution, 0) / data.conversations.length
    },
    {
      criteria: 'Guidelines',
      score: data.conversations.reduce((sum, c) => sum + c.detailed_scores.guidelines, 0) / data.conversations.length
    },
    {
      criteria: 'Timeliness',
      score: data.conversations.reduce((sum, c) => sum + c.detailed_scores.timeliness, 0) / data.conversations.length
    }
  ];

  return (
    <Box>
      {/* Advanced Features Coming Soon Banner */}
      <Alert
        severity="info"
        icon={<Grade />}
        sx={{ mb: 3, bgcolor: '#f3e5f5', borderLeft: '4px solid #9c27b0' }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            ⭐ AI-Powered Quality Analysis Coming Soon!
          </Typography>
          <Typography variant="body2">
            Currently showing quality scores based on resolution status, CSAT, and response times.
            Next release will include: AI-powered sentiment analysis, Intent detection, Compliance checks, and Advanced conversation insights.
          </Typography>
        </Box>
      </Alert>

      {/* Overview Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Average Quality Score
              </Typography>
              <Typography variant="h3" color="primary">
                {data.average_score.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Out of 100
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Conversations Reviewed
              </Typography>
              <Typography variant="h3" color="primary">
                {data.conversations.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                AI-powered analysis
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle color="success" />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Excellent (A+B)
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {data.grade_distribution.A + data.grade_distribution.B}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning color="error" />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Needs Improvement
                  </Typography>
                  <Typography variant="h4" color="error">
                    {data.grade_distribution.D + data.grade_distribution.F}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quality Criteria Radar */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quality Criteria Analysis
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Average scores across all quality dimensions
            </Typography>
            <Box height={350}>
              <ResponsiveRadar
                data={radarData}
                keys={['score']}
                indexBy="criteria"
                maxValue={100}
                margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
                borderColor={{ from: 'color' }}
                gridLabelOffset={20}
                dotSize={10}
                dotColor={{ theme: 'background' }}
                dotBorderWidth={2}
                colors={{ scheme: 'nivo' }}
                blendMode="multiply"
                motionConfig="wobbly"
                fillOpacity={0.25}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Grade Distribution */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Grade Distribution
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Quality grades across all reviewed conversations
            </Typography>
            <Chart
              options={gradeChartOptions}
              series={[
                data.grade_distribution.A,
                data.grade_distribution.B,
                data.grade_distribution.C,
                data.grade_distribution.D,
                data.grade_distribution.F
              ]}
              type="donut"
              height={300}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Top Performing Agents */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top Performing Agents (Quality Scores)
        </Typography>
        <Grid container spacing={2}>
          {data.top_performers.map((agent, index) => (
            <Grid item xs={12} md={4} key={agent.agent_name}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    {index === 0 && <Grade sx={{ color: '#FFD700', fontSize: 40 }} />}
                    {index === 1 && <Grade sx={{ color: '#C0C0C0', fontSize: 40 }} />}
                    {index === 2 && <Grade sx={{ color: '#CD7F32', fontSize: 40 }} />}
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {agent.agent_name}
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {agent.avg_score.toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {agent.conversations_reviewed} conversations
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Improvement Areas */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Improvement Priorities
        </Typography>
        <List>
          {data.improvement_areas.map((area) => (
            <ListItem key={area.criteria}>
              <ListItemIcon>
                {area.priority === 'High' && <ErrorIcon color="error" />}
                {area.priority === 'Medium' && <Warning color="warning" />}
                {area.priority === 'Low' && <CheckCircle color="success" />}
              </ListItemIcon>
              <ListItemText
                primary={area.criteria}
                secondary={`Average Score: ${area.avg_score.toFixed(1)}`}
              />
              <Chip
                label={`${area.priority} Priority`}
                size="small"
                color={
                  area.priority === 'High' ? 'error' :
                  area.priority === 'Medium' ? 'warning' : 'success'
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Recent Conversations */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Quality Assessments
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell align="center">Overall Score</TableCell>
                <TableCell align="center">Grade</TableCell>
                <TableCell align="center">Completeness</TableCell>
                <TableCell align="center">Tone</TableCell>
                <TableCell align="center">Resolution</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.conversations.slice(0, 10).map((conv) => (
                <TableRow key={conv.conversation_id} hover>
                  <TableCell>#{conv.conversation_id}</TableCell>
                  <TableCell>{conv.contact_name}</TableCell>
                  <TableCell>{conv.agent_name}</TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {conv.overall_score.toFixed(1)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={conv.overall_score}
                        sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={conv.grade}
                      size="small"
                      sx={{
                        bgcolor: getGradeColor(conv.grade),
                        color: '#fff',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {conv.detailed_scores.completeness.toFixed(0)}
                  </TableCell>
                  <TableCell align="center">
                    {conv.detailed_scores.tone.toFixed(0)}
                  </TableCell>
                  <TableCell align="center">
                    {conv.detailed_scores.resolution.toFixed(0)}
                  </TableCell>
                  <TableCell>{conv.date}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Quality Criteria Explanation */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>
          🤖 Quality Scoring Criteria
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Response Completeness (25%)
            </Typography>
            <Typography variant="caption">
              AI analyzes if all customer questions were answered comprehensively
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Tone & Professionalism (20%)
            </Typography>
            <Typography variant="caption">
              Sentiment analysis on agent messages for empathy and professionalism
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Resolution Effectiveness (25%)
            </Typography>
            <Typography variant="caption">
              Correlation with issue resolution and CSAT scores
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Adherence to Guidelines (15%)
            </Typography>
            <Typography variant="caption">
              Checklist for greeting, empathy, solution, and closing
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Response Timeliness (15%)
            </Typography>
            <Typography variant="caption">
              Response time performance against SLA targets
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ConversationQualityScoring;
